// Arkus policy add-on: Volto Light Theme + kitconcept blocks, Arkus branding.
// Fonts: the old theme used Myriad Pro (body) and DIN (headings/quotes), both
// commercial; we self-host the closest open (OFL) fonts instead:
// Source Sans 3 (Adobe's open sibling of Myriad) and Barlow (DIN-like grotesk).
import '@fontsource/source-sans-3/400.css';
import '@fontsource/source-sans-3/400-italic.css';
import '@fontsource/source-sans-3/600.css';
import '@fontsource/source-sans-3/700.css';
import '@fontsource/barlow/500.css';
import '@fontsource/barlow/600.css';
import '@fontsource/barlow/700.css';
import '@fontsource/barlow/800.css';

import { ArkusFooter, ArkusCopyright } from './components/ArkusFooter';
import EventView from './components/EventView';
import NewsItemView from './components/NewsItemView';
import EventMetadataView from './components/EventMetadataView';
import ArkusAnalytics from './components/ArkusAnalytics';
import KeywordManagerLink from './components/KeywordManagerLink';
import { StoryLogoView, StoryLogoEdit } from './components/StoryLogo';
import loadable from '@loadable/component';

const always = () => true;

const applyConfig = (config) => {
  config.settings = {
    ...config.settings,
    isMultilingual: false,
    supportedLanguages: ['en'],
    defaultLanguage: 'en',
  };

  // Footer: replace VLT's core footer + "Powered by Plone" colophon with the
  // Arkus footer of the old site. (A predicate makes these win over the VLT
  // registrations of the same name.)
  config.registerSlotComponent({
    slot: 'footer',
    name: 'coreFooter',
    component: ArkusFooter,
    predicates: [always],
  });
  config.registerSlotComponent({
    slot: 'postFooter',
    name: 'Colophon',
    component: ArkusCopyright,
    predicates: [always],
  });

  // Event view: whole-day events without "12:00 AM", fixed time zone.
  config.views.contentTypesViews.Event = EventView;

  // Event metadata block: ICS link via /++api++ (relative) instead of the SSR internal URL.
  if (config.blocks.blocksConfig.eventMetadata) {
    config.blocks.blocksConfig.eventMetadata = {
      ...config.blocks.blocksConfig.eventMetadata,
      view: EventMetadataView,
    };
  }

  // News items: author byline + author box (old arkusinc.policy look).
  config.views.contentTypesViews['News Item'] = NewsItemView;

  // Cookie consent (volto-gdpr-privacy): GA is only mounted after the visitor
  // accepts the "Google Analytics" choice; ArkusAnalytics adds the host check.
  config.settings['volto-gdpr-privacy'] = {
    ...(config.settings['volto-gdpr-privacy'] || {}),
    settings: {
      ...(config.settings['volto-gdpr-privacy']?.settings || {}),
      prof_GANALYTICS: { component: ArkusAnalytics },
    },
  };

  // @eeacms/volto-anchors (pulled in by the tabs block) turns slate headings into
  // linked headings with a hover icon; keep the site's plain headings.
  config.settings.slate.useLinkedHeadings = false;

  // Site Setup: "Keyword Manager" link (Jason, 2026-10-04 5:36 PM). Opens the classic-UI
  // Products.PloneKeywordManager on https://admin.arkusinc.com in a new tab (see KeywordManagerLink).
  config.settings.controlpanels = [
    ...(config.settings.controlpanels || []),
    {
      '@id': '/keyword-manager',
      group: 'Add-on Configuration',
      title: 'Keyword Manager',
    },
  ];
  config.settings.controlPanelsIcons = {
    ...(config.settings.controlPanelsIcons || {}),
    'keyword-manager': config.settings.controlPanelsIcons?.['dexterity-types'] || config.settings.controlPanelsIcons?.default,
  };
  config.addonRoutes = [
    ...(config.addonRoutes || []),
    { path: '/controlpanel/keyword-manager', component: KeywordManagerLink },
  ];

  // Speed (Jason, 2026-10-03, speed fix 4): main-menu depth 2 instead of VLT's 3.
  // Depth 3 made Plone walk /archive/<year>/<post> (1,000+ items) on every page
  // render and every in-site click (~0.3 s), for a menu that shows 8 entries.
  config.settings.navDepth = 2;

  // Form block fix (2026-10-03): volto-form-block 3.19 edits its fields through
  // volto-subblocks 2.1.1, which asks Volto for the lazy libraries 'reactDnd' and
  // 'reactDndHtml5Backend'. Volto 18+ no longer registers them (it moved to
  // @dnd-kit), so the block crashed in the editor with "Invalid lib or bundle name
  // reactDnd,reactDndHtml5Backend" -> "Block error: The form block ... has
  // encountered an error". Register them again (same definitions as Volto 17).
  config.settings.loadables = {
    ...config.settings.loadables,
    reactDnd: loadable.lib(() => import('react-dnd')),
    reactDndHtml5Backend: loadable.lib(() => import('react-dnd-html5-backend')),
  };

  // Success stories: "Client logo" block = the story's own Client logo field, full colour, beside
  // the facts list (StoryLogo.jsx, 2026-10-04). Only offered on success_story pages.
  config.blocks.blocksConfig.arkusStoryLogo = {
    id: 'arkusStoryLogo',
    title: 'Client logo',
    icon: config.blocks.blocksConfig.image?.icon,
    group: 'media',
    view: StoryLogoView,
    edit: StoryLogoEdit,
    restricted: ({ properties }) => properties?.['@type'] !== 'success_story',
    mostUsed: false,
    blockHasOwnFocusManagement: false,
    sidebarTab: 0,
  };

  return config;
};

export default applyConfig;
